/** Port of app/components/log/actions/StreakActions.ts (modular SDK). */
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import moment from 'moment'
import { db } from '@/lib/firebase'
import { track } from '@/lib/analytics'

const STREAK_MILESTONES = [3, 7, 14, 30, 60, 100]

const trackStreakMilestone = (newStreak: number): void => {
  if (STREAK_MILESTONES.includes(newStreak)) track('streak_milestone', { streak_days: newStreak })
}

/**
 * Updates the user's streak based on the provided timestamp.
 * If timestamp is provided, it updates the streak based on a new log.
 * If timestamp is null, it performs a streak continuity check on startup.
 */
export const updateStreak = async (uid: string, timestamp: string | null = null): Promise<void> => {
  const ref = doc(db, 'userSettings', uid)
  try {
    const snap = await getDoc(ref)
    if (!snap.exists()) return
    const data = snap.data() as { streakCount?: number; lastLoggedDate?: string; longestStreak?: number }
    const { streakCount = 0, lastLoggedDate = '', longestStreak = 0 } = data || {}

    const today = moment().startOf('day')
    const lastLogged = lastLoggedDate ? moment(lastLoggedDate, 'YYYY-MM-DD').startOf('day') : null

    if (!timestamp) {
      if (lastLogged && today.diff(lastLogged, 'days') > 1) {
        await updateDoc(ref, { streakCount: 0 })
      }
      return
    }

    const loggedDate = moment(timestamp).startOf('day')
    if (!loggedDate.isSame(today, 'day')) return
    if (lastLogged && loggedDate.isSame(lastLogged, 'day')) return

    if (lastLogged) {
      const daysDifference = loggedDate.diff(lastLogged, 'days')
      if (daysDifference < 0) return
      if (daysDifference === 1) {
        const newStreakCount = streakCount + 1
        const newLongestStreak = Math.max(newStreakCount, longestStreak)
        await updateDoc(ref, { streakCount: newStreakCount, lastLoggedDate: loggedDate.format('YYYY-MM-DD'), longestStreak: newLongestStreak })
        trackStreakMilestone(newStreakCount)
      } else {
        await updateDoc(ref, { streakCount: 1, lastLoggedDate: loggedDate.format('YYYY-MM-DD') })
      }
    } else {
      await updateDoc(ref, { streakCount: 1, lastLoggedDate: loggedDate.format('YYYY-MM-DD'), longestStreak: Math.max(1, longestStreak) })
    }
  } catch (error) {
    console.error('Error updating streak: ', error)
  }
}
